// /rules — analyzer rule-catalog browser with search + language filter (T034).
// T154: group rule cards by language family with collapsible sections, icon
// legend, and per-language counts in section headers.
// T182b: third tab "Marketplace" — community rules with signed badge, author,
// downloads, version, and an Install button that calls the T182a endpoint.
//
// Why a dedicated page:
//   The rule catalog is the authoritative answer to "what does orbital-lint
//   actually look for?" — it's what users browse before tuning a quality
//   profile or hunting for the rule ID behind a finding. Embedding it inside
//   the issue detail drawer (T033) only solves the per-finding case; this
//   page solves the discovery case.
//
// Surface:
//   - Header: page title + total rule count (shown / total when filtered).
//   - Tab bar: "Built-in" (default) | "Marketplace" — switched via ?tab=.
//   - Built-in tab:
//       Filter bar: free-text search + language chips + severity chips + kind
//       chips, all mirrored into the URL (?q=, ?lang=, ?severity=, ?kind=).
//       Grouped result list: rule cards grouped by language family.
//       Icon legend: compact family → icon reference row.
//   - Marketplace tab:
//       Search bar (?mq=) — filters by name / author / tag.
//       Rule cards: signed/unverified badge, author, downloads, version.
//       Install button → POST /api/v1/admin/marketplace/install.
//
// Empty / error states match the rest of the FE: a friendly `orb-card`
// panel that names the most likely cause.

import React, { ReactNode, useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import useSWRInfinite from 'swr/infinite';
import {
  AlertTriangle,
  Book,
  BookOpen,
  Bug,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Cloud,
  Code2,
  Coffee,
  Cpu,
  Download,
  Eye,
  ExternalLink,
  Filter,
  Globe,
  KeyRound,
  Loader2,
  Network,
  Search,
  ShieldAlert,
  Sprout,
  Store,
  Wand2,
  X,
} from 'lucide-react';
import {
  ApiError,
  ISSUE_KINDS,
  ISSUE_SEVERITIES,
  IssueKind,
  IssueSeverity,
  MARKETPLACE_RULES_URL,
  MarketplaceRule,
  MarketplaceRulesResponse,
  Rule,
  RuleFacets,
  RuleSearchResponse,
  RuleSummary,
  buildRuleDetailURL,
  buildRulesSearchURL,
  fetcher,
  marketplaceApi,
} from '../lib/api';
import { Dialog, DialogFooter, DialogHeader } from '../components/ui/Dialog';
import { PageHeader } from '../components/ui';
import { safeHref } from '../lib/safeHref'

// ---------------------------------------------------------------------------
// Tone tables — kept byte-for-byte in sync with Issues.tsx / the drawer so a
// rule's severity chip on /rules and a finding's severity chip on /issues
// read identically.
// ---------------------------------------------------------------------------

const SEVERITY_TONE: Record<IssueSeverity, string> = {
  INFO: 'bg-bg-inset text-fg-muted border-border',
  MINOR: 'bg-accent-muted text-accent border-accent/30',
  MAJOR: 'bg-warning/10 text-warning border-warning/40',
  CRITICAL: 'bg-danger/10 text-danger border-danger/40',
  BLOCKER: 'bg-danger/20 text-danger border-danger/60',
};

const KIND_LABEL: Record<IssueKind, string> = {
  BUG: 'Bug',
  CODE_SMELL: 'Smell',
  VULNERABILITY: 'Vuln',
  SECURITY_HOTSPOT: 'Hotspot',
  SECRET: 'Secret',
};

const KIND_ICON: Record<IssueKind, ReactNode> = {
  BUG: <Bug className="h-3 w-3" aria-hidden="true" />,
  CODE_SMELL: <Sprout className="h-3 w-3" aria-hidden="true" />,
  VULNERABILITY: <ShieldAlert className="h-3 w-3" aria-hidden="true" />,
  SECURITY_HOTSPOT: <Eye className="h-3 w-3" aria-hidden="true" />,
  SECRET: <KeyRound className="h-3 w-3" aria-hidden="true" />,
};

// Human-readable labels for the language values shipped by the default
// registry. Anything not in this map renders as the raw value, so a new
// analyzer just shows up automatically.
const LANGUAGE_LABEL: Record<string, string> = {
  '*': 'Any',
  // general
  go: 'Go',
  py: 'Python',
  // web
  js: 'JavaScript',
  ts: 'TypeScript',
  html: 'HTML',
  css: 'CSS',
  xml: 'XML',
  php: 'PHP',
  rb: 'Ruby',
  // systems
  c: 'C',
  cpp: 'C++',
  rs: 'Rust',
  shell: 'Shell',
  sh: 'Shell',
  swift: 'Swift',
  // jvm
  java: 'Java',
  kotlin: 'Kotlin',
  scala: 'Scala',
  // dotnet
  csharp: 'C#',
  cs: 'C#',
  vbdotnet: 'VB.NET',
  // cloud / infra
  docker: 'Dockerfile',
  yaml: 'YAML/K8s',
  sql: 'SQL',
  terraform: 'Terraform',
  // legacy entries
  md: 'Markdown',
};

// ---------------------------------------------------------------------------
// Language-family grouping (T154)
// ---------------------------------------------------------------------------

type FamilyKey = 'web' | 'systems' | 'jvm' | 'dotnet' | 'cloud' | 'iac' | 'secrets' | 'dsl';

interface FamilyMeta {
  key: FamilyKey;
  label: string;
  description: string;
  icon: ReactNode;
}

// Ordered list determines section render order on the page.
const FAMILY_META: FamilyMeta[] = [
  {
    key: 'web',
    label: 'Web',
    description: 'JavaScript, HTML, CSS, XML, PHP, Ruby, Python',
    icon: <Globe className="h-4 w-4" aria-hidden="true" />,
  },
  {
    key: 'systems',
    label: 'Systems',
    description: 'C, C++, Rust, Go, Shell, Swift',
    icon: <Cpu className="h-4 w-4" aria-hidden="true" />,
  },
  {
    key: 'jvm',
    label: 'JVM',
    description: 'Java, Kotlin, Scala',
    icon: <Coffee className="h-4 w-4" aria-hidden="true" />,
  },
  {
    key: 'dotnet',
    label: '.NET',
    description: 'C#, VB.NET',
    icon: <Code2 className="h-4 w-4" aria-hidden="true" />,
  },
  {
    key: 'cloud',
    label: 'Cloud',
    description: 'Dockerfile, YAML/K8s, SQL',
    icon: <Cloud className="h-4 w-4" aria-hidden="true" />,
  },
  {
    key: 'iac',
    label: 'IaC',
    description: 'Terraform and infrastructure-as-code',
    icon: <Network className="h-4 w-4" aria-hidden="true" />,
  },
  {
    key: 'secrets',
    label: 'Secrets',
    description: 'Secrets and credential detection (all file types)',
    icon: <KeyRound className="h-4 w-4" aria-hidden="true" />,
  },
  {
    key: 'dsl',
    label: 'DSL',
    description: 'User-defined custom rules',
    icon: <Wand2 className="h-4 w-4" aria-hidden="true" />,
  },
];

// Maps rule.language → family key. Unknown languages fall through to 'dsl'
// (catch-all for custom/DSL rules) so nothing is silently dropped.
const LANGUAGE_FAMILY: Record<string, FamilyKey> = {
  // web
  js: 'web',
  ts: 'web',
  html: 'web',
  css: 'web',
  xml: 'web',
  php: 'web',
  rb: 'web',
  py: 'web',
  // systems
  c: 'systems',
  cpp: 'systems',
  rs: 'systems',
  go: 'systems',
  shell: 'systems',
  sh: 'systems',
  swift: 'systems',
  // jvm
  java: 'jvm',
  kotlin: 'jvm',
  scala: 'jvm',
  // dotnet
  csharp: 'dotnet',
  cs: 'dotnet',
  vbdotnet: 'dotnet',
  // cloud
  docker: 'cloud',
  yaml: 'cloud',
  sql: 'cloud',
  // iac
  terraform: 'iac',
  // secrets — '*' means "runs on every file regardless of language"
  '*': 'secrets',
};

interface LangCount {
  lang: string;
  count: number;
}

interface FamilyGroup {
  family: FamilyMeta;
  rules: RuleSummary[];
  langCounts: LangCount[];
}

function computeFamilyGroups(items: RuleSummary[]): FamilyGroup[] {
  const familyRules = new Map<FamilyKey, RuleSummary[]>();
  const familyLangCounts = new Map<FamilyKey, Map<string, number>>();

  for (const r of items) {
    const lang = r.language || '';
    const fk: FamilyKey = LANGUAGE_FAMILY[lang] ?? 'dsl';
    if (!familyRules.has(fk)) {
      familyRules.set(fk, []);
      familyLangCounts.set(fk, new Map());
    }
    familyRules.get(fk)!.push(r);
    const lc = familyLangCounts.get(fk)!;
    lc.set(lang, (lc.get(lang) ?? 0) + 1);
  }

  return FAMILY_META
    .filter((f) => (familyRules.get(f.key)?.length ?? 0) > 0)
    .map((f) => ({
      family: f,
      rules: familyRules.get(f.key) ?? [],
      langCounts: Array.from(
        familyLangCounts.get(f.key) ?? new Map<string, number>(),
        ([lang, count]) => ({ lang, count }),
      ).sort((a, b) => b.count - a.count),
    }));
}

// ---------------------------------------------------------------------------
// Tab types
// ---------------------------------------------------------------------------

type ActiveTab = 'built-in' | 'marketplace';

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

// RULES_PAGE_SIZE mirrors the backend's rulesDefaultLimit
// (api/internal/handlers/rules.go, T838).
const RULES_PAGE_SIZE = 50;

const EMPTY_FACETS: RuleFacets = { language: [], severity: [], kind: [] };

export function Rules() {
  const [search, setSearch] = useSearchParams();
  const tab: ActiveTab = search.get('tab') === 'marketplace' ? 'marketplace' : 'built-in';

  const q = search.get('q') ?? '';
  const language = search.get('lang') ?? '';
  const severityFilter = parseSeverity(search.get('severity'));
  const kindFilter = parseKind(search.get('kind'));
  const selectedRuleID = search.get('rule') ?? '';

  // Use useDeferredValue so a fast typist doesn't fire a new request on
  // every keystroke.
  const deferredQ = useDeferredValue(q);

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(search);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearch(next, { replace: true });
  };

  const setTab = (t: ActiveTab) => {
    const next = new URLSearchParams(search);
    if (t === 'built-in') next.delete('tab');
    else next.set('tab', t);
    setSearch(next, { replace: true });
  };

  const clearSelectedRule = () => {
    const next = new URLSearchParams(search);
    next.delete('rule');
    setSearch(next, { replace: true });
  };

  const hasFilters =
    deferredQ !== '' ||
    language !== '' ||
    severityFilter !== undefined ||
    kindFilter !== undefined;

  // Search-first, server-paginated (T838): with no filter at all the
  // backend still answers this same key with `items: []` — only `total`
  // and `facets` populated — so the very first page load is one small
  // request and the page renders no rule rows until the user searches or
  // picks a facet chip (any single facet, even with no text query, already
  // counts as a filter and returns a real page).
  const baseParams = useMemo(
    () => ({
      q: deferredQ || undefined,
      language: language || undefined,
      severity: severityFilter,
      kind: kindFilter,
      limit: RULES_PAGE_SIZE,
    }),
    [deferredQ, language, severityFilter, kindFilter],
  );

  const getKey = useCallback(
    (pageIndex: number, prevPage: RuleSearchResponse | null): string | null => {
      if (pageIndex === 0) return buildRulesSearchURL({ ...baseParams, offset: 0 });
      if (!prevPage) return null;
      const loaded = prevPage.offset + prevPage.items.length;
      if (loaded >= prevPage.total) return null;
      return buildRulesSearchURL({ ...baseParams, offset: loaded });
    },
    [baseParams],
  );

  const {
    data: pages,
    error,
    isLoading,
    isValidating,
    size,
    setSize,
  } = useSWRInfinite<RuleSearchResponse>(getKey, fetcher, { revalidateFirstPage: false });

  const items: RuleSummary[] = useMemo(
    () => (pages ?? []).flatMap((p) => p.items ?? []),
    [pages],
  );
  const facets = pages?.[0]?.facets ?? EMPTY_FACETS;
  const total = pages?.[0]?.total ?? 0;
  const lastPage = pages?.[pages.length - 1];
  const hasMore = Boolean(lastPage && lastPage.offset + lastPage.items.length < lastPage.total);
  const isLoadingMore = isValidating && pages !== undefined && size > pages.length;
  const loadMore = () => setSize((s) => s + 1);

  const ruleDetail = useSWR<Rule>(
    selectedRuleID ? buildRuleDetailURL(selectedRuleID) : null,
    fetcher,
    { revalidateOnFocus: false },
  );

  const showCount = tab === 'built-in' && !isLoading && !error;
  let countMeta: string | null = null;
  if (showCount) {
    countMeta = hasFilters ? `${items.length} of ${total} rules` : `${total} rules`;
  }

  return (
    <div className="max-w-5xl mx-auto p-8 w-full">
      <PageHeader
        eyebrow="orbital · rules"
        title="Rule catalog"
        meta={countMeta}
        description={
          <>
            Every rule the analyzers will run, straight from the active server
            build. Search by id or keyword, then narrow by language, severity,
            or kind to focus on a slice you care about.
          </>
        }
      />

      <TabBar activeTab={tab} onTabChange={setTab} />

      {tab === 'built-in' ? (
        <>
          <FilterBar
            q={q}
            onQ={(value) => setParam('q', value)}
            language={language}
            onLanguage={(value) => setParam('lang', value)}
            facets={facets}
            severity={severityFilter}
            onSeverity={(value) => setParam('severity', value ?? '')}
            kind={kindFilter}
            onKind={(value) => setParam('kind', value ?? '')}
          />

          <div className="mt-6">
            <BuiltInRulesBody
              isLoading={isLoading}
              error={error}
              items={items}
              hasFilters={hasFilters}
              total={total}
              hasMore={hasMore}
              isLoadingMore={isLoadingMore}
              onLoadMore={loadMore}
            />
          </div>
        </>
      ) : (
        <MarketplaceTab search={search} setParam={setParam} />
      )}

      {selectedRuleID ? (
        <RuleDetailPanel
          rule={ruleDetail.data}
          isLoading={ruleDetail.isLoading}
          ruleID={selectedRuleID}
          onClose={clearSelectedRule}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Built-in rules body
// ---------------------------------------------------------------------------

interface BuiltInRulesBodyProps {
  readonly isLoading: boolean;
  readonly error: unknown;
  readonly items: RuleSummary[];
  readonly hasFilters: boolean;
  readonly total: number;
  readonly hasMore: boolean;
  readonly isLoadingMore: boolean;
  readonly onLoadMore: () => void;
}

function BuiltInRulesBody({
  isLoading,
  error,
  items,
  hasFilters,
  total,
  hasMore,
  isLoadingMore,
  onLoadMore,
}: BuiltInRulesBodyProps) {
  if (isLoading) return <Loading label="Loading rules…" />;
  if (error) return <RequestError error={error} />;
  if (!hasFilters) return <SearchPrompt totalRules={total} />;
  if (items.length === 0) return <EmptyState hasFilters={hasFilters} totalRules={total} />;
  return (
    <>
      <GroupedRuleList rules={items} />
      {hasMore ? <LoadMoreButton loading={isLoadingMore} onClick={onLoadMore} /> : null}
    </>
  );
}

// Shown on page open (and whenever every filter is cleared) instead of a
// rule list — rendering all ~800 rules before the user has expressed any
// interest is exactly what made /rules slow (T838). Mirrors SonarQube's own
// search-first rule catalog: nothing renders until a search or facet click.
function SearchPrompt({ totalRules }: { readonly totalRules: number }) {
  return (
    <div className="orb-card p-8 text-center">
      <div className="text-fg-subtle inline-flex items-center justify-center mb-2">
        <Search className="h-5 w-5" aria-hidden="true" />
      </div>
      <h2 className="font-display text-lg font-semibold text-fg mb-2">
        Search {totalRules} rules.
      </h2>
      <p className="text-fg-muted text-sm max-w-md mx-auto">
        Type an id or keyword above, or pick a language / severity / kind
        chip below, to see matching rules.
      </p>
    </div>
  );
}

function LoadMoreButton({
  loading,
  onClick,
}: {
  readonly loading: boolean;
  readonly onClick: () => void;
}) {
  return (
    <div className="mt-4 flex justify-center">
      <button
        type="button"
        onClick={onClick}
        disabled={loading}
        className="
          inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium
          bg-bg-inset text-fg-muted border border-border
          hover:text-fg hover:bg-bg-hover transition-colors
          disabled:opacity-50 disabled:cursor-not-allowed
        "
      >
        {loading ? (
          <>
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
            Loading…
          </>
        ) : (
          'Load more'
        )}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab bar (T182b)
// ---------------------------------------------------------------------------

function TabBar({
  activeTab,
  onTabChange,
}: {
  readonly activeTab: ActiveTab;
  readonly onTabChange: (t: ActiveTab) => void;
}) {
  return (
    <div className="flex items-center gap-0 border-b border-border mb-6">
      <TabButton
        active={activeTab === 'built-in'}
        onClick={() => onTabChange('built-in')}
        icon={<Book className="h-3.5 w-3.5" aria-hidden="true" />}
        label="Built-in"
      />
      <TabButton
        active={activeTab === 'marketplace'}
        onClick={() => onTabChange('marketplace')}
        icon={<Store className="h-3.5 w-3.5" aria-hidden="true" />}
        label="Marketplace"
      />
    </div>
  );
}

function TabButton({
  active,
  onClick,
  icon,
  label,
}: {
  readonly active: boolean;
  readonly onClick: () => void;
  readonly icon: ReactNode;
  readonly label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors',
        active
          ? 'border-accent text-accent'
          : 'border-transparent text-fg-muted hover:text-fg hover:border-border',
      ].join(' ')}
    >
      {icon}
      {label}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Marketplace tab (T182b)
// ---------------------------------------------------------------------------

function MarketplaceTab({
  search,
  setParam,
}: {
  readonly search: URLSearchParams;
  readonly setParam: (key: string, value: string) => void;
}) {
  const { data, error, isLoading } = useSWR<MarketplaceRulesResponse>(
    MARKETPLACE_RULES_URL,
    fetcher,
    { revalidateOnFocus: false },
  );

  const mq = search.get('mq') ?? '';
  const deferredMq = useDeferredValue(mq);

  // Track install state in session-local React state. The server's
  // `already_active` field handles idempotency on re-install.
  const [installed, setInstalled] = useState<Set<string>>(new Set());
  const [installing, setInstalling] = useState<Set<string>>(new Set());
  const [installErrors, setInstallErrors] = useState<Record<string, string>>({});
  // T652: an unverified rule needs an explicit org-admin acknowledgement
  // before install — this holds the rule pending that confirmation, `null`
  // when no dialog is open.
  const [confirmRule, setConfirmRule] = useState<MarketplaceRule | null>(null);

  const doInstall = async (ruleID: string, override: boolean) => {
    setInstalling((prev) => new Set(prev).add(ruleID));
    setInstallErrors((prev) => {
      const next = { ...prev };
      delete next[ruleID];
      return next;
    });
    try {
      await marketplaceApi.install(ruleID, override);
      setInstalled((prev) => new Set(prev).add(ruleID));
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : 'Install failed';
      setInstallErrors((prev) => ({ ...prev, [ruleID]: msg }));
    } finally {
      setInstalling((prev) => {
        const next = new Set(prev);
        next.delete(ruleID);
        return next;
      });
    }
  };

  // Verified rules install immediately; unverified ones require the T652
  // confirm dialog so the ⚠ unverified state is unmissable at the moment
  // that matters — right before the org-admin commits to installing it.
  const handleInstall = (rule: MarketplaceRule) => {
    if (!rule.is_verified) {
      setConfirmRule(rule);
      return;
    }
    void doInstall(rule.rule_id, false);
  };

  const confirmOverrideInstall = () => {
    if (!confirmRule) return;
    void doInstall(confirmRule.rule_id, true);
    setConfirmRule(null);
  };

  const filtered = useMemo(() => {
    const items = data?.rules ?? [];
    const q = deferredMq.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (r) =>
        r.name.toLowerCase().includes(q) ||
        r.author.toLowerCase().includes(q) ||
        r.rule_id.toLowerCase().includes(q) ||
        r.tags.some((t) => t.toLowerCase().includes(q)),
    );
  }, [data, deferredMq]);

  const total = data?.total ?? 0;

  return (
    <div>
      <section aria-label="Marketplace search" className="orb-card p-4 mb-6">
        <label className="block">
          <span className="orb-section-title mb-1.5 inline-flex items-center gap-1.5 text-fg-muted">
            <Search className="h-3.5 w-3.5" aria-hidden="true" />
            Search marketplace
          </span>
          <input
            type="search"
            value={mq}
            onChange={(e) => setParam('mq', e.target.value)}
            placeholder="name, author, tag…"
            className="
              w-full px-3 py-2 rounded-md text-sm
              bg-bg-inset border border-border text-fg
              placeholder:text-fg-subtle
              focus:border-accent/60 focus:bg-bg-elevated
              transition-colors
            "
            aria-label="Search marketplace rules"
          />
        </label>
        {!isLoading && !error ? (
          <p className="mt-2 text-xs text-fg-muted">
            {deferredMq
              ? `${filtered.length} of ${total} rules`
              : `${total} rules available`}
          </p>
        ) : null}
      </section>

      {isLoading ? <Loading label="Loading marketplace…" /> : null}
      {!isLoading && error ? <RequestError error={error} /> : null}
      {!isLoading && !error && filtered.length === 0 ? (
        <MarketplaceEmpty hasQuery={deferredMq !== ''} />
      ) : null}
      {!isLoading && !error && filtered.length > 0 ? (
        <ul className="orb-card divide-y divide-border" aria-label="Marketplace rules">
          {filtered.map((r) => (
            <MarketplaceRuleCard
              key={r.rule_id}
              rule={r}
              installed={installed.has(r.rule_id)}
              installing={installing.has(r.rule_id)}
              installError={installErrors[r.rule_id]}
              onInstall={() => handleInstall(r)}
            />
          ))}
        </ul>
      ) : null}

      <UnverifiedInstallDialog
        rule={confirmRule}
        onCancel={() => setConfirmRule(null)}
        onConfirm={confirmOverrideInstall}
      />
    </div>
  );
}

// UnverifiedInstallDialog (T652) is the "explicit org-admin override" step:
// installing a rule whose signature couldn't be verified means running
// unreviewed logic in the org's quality gates, so it gets its own
// confirmation rather than silently succeeding like a verified install.
function UnverifiedInstallDialog({
  rule,
  onCancel,
  onConfirm,
}: {
  readonly rule: MarketplaceRule | null;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
}) {
  return (
    <Dialog open={rule !== null} onClose={onCancel}>
      <DialogHeader
        eyebrow="Unverified rule"
        title={
          <span className="inline-flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-warning" aria-hidden="true" />
            Install without a verified signature?
          </span>
        }
      />
      <p className="text-sm text-fg-muted leading-relaxed">
        <span className="font-medium text-fg">{rule?.name}</span> ({rule?.rule_id}) could
        not be signature-verified against the marketplace's public key. Installing it runs
        unreviewed rule logic in your quality gates. This override is recorded as an audit
        event.
      </p>
      <DialogFooter>
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1.5 rounded-md text-sm font-medium text-fg-muted hover:bg-bg-inset transition-colors"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onConfirm}
          className="
            inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium
            bg-warning/10 text-warning border border-warning/40
            hover:bg-warning/20 transition-colors
          "
        >
          <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
          Install anyway
        </button>
      </DialogFooter>
    </Dialog>
  );
}

function MarketplaceRuleCard({
  rule,
  installed,
  installing,
  installError,
  onInstall,
}: {
  readonly rule: MarketplaceRule;
  readonly installed: boolean;
  readonly installing: boolean;
  readonly installError?: string;
  readonly onInstall: () => void;
}) {
  const severity = asSeverity(rule.severity);
  const kind = asKind(rule.kind);
  const tone = severity ? SEVERITY_TONE[severity] : SEVERITY_TONE.INFO;
  const langLabel = LANGUAGE_LABEL[rule.language] ?? rule.language;
  const preview = useMemo(() => firstParagraph(rule.description), [rule.description]);

  return (
    <li className="px-5 py-4 text-sm">
      <div className="flex items-start gap-3 flex-wrap">
        {/* Verified / unverified badge */}
        {rule.is_verified ? (
          <span
            className="orb-badge border bg-success/10 text-success border-success/30 shrink-0"
            title="Signature verified"
          >
            <CheckCircle2 className="h-3 w-3" aria-hidden="true" />
            <span>signed</span>
          </span>
        ) : (
          <span
            className="orb-badge border bg-warning/10 text-warning border-warning/40 shrink-0"
            title="Signature not verified"
          >
            <AlertTriangle className="h-3 w-3" aria-hidden="true" />
            <span>unverified</span>
          </span>
        )}

        <span className={`orb-badge border shrink-0 ${tone}`}>
          {severity ?? rule.severity ?? 'INFO'}
        </span>
        {kind ? (
          <span
            className="orb-badge border bg-nebula/10 text-nebula border-nebula/30 shrink-0"
            title={kind.replace('_', ' ').toLowerCase()}
          >
            {KIND_ICON[kind]}
            <span>{KIND_LABEL[kind]}</span>
          </span>
        ) : null}
        <span
          className="orb-badge border bg-bg-inset text-fg-muted border-border shrink-0"
          title={`Language: ${langLabel}`}
        >
          {langLabel}
        </span>

        <div className="flex-1 min-w-0">
          <p className="text-fg font-medium leading-snug">{rule.name}</p>
          <p className="orb-code text-xs text-fg-muted mt-0.5">{rule.rule_id}</p>
          <div className="flex items-center gap-2 mt-1 text-xs text-fg-subtle flex-wrap">
            <span title="Author">{rule.author}</span>
            <span aria-hidden="true">·</span>
            <span title="Version">v{rule.version}</span>
            <span aria-hidden="true">·</span>
            <span
              className="inline-flex items-center gap-0.5"
              title="Total downloads"
            >
              <Download className="h-3 w-3" aria-hidden="true" />
              {rule.downloads.toLocaleString()}
            </span>
          </div>
          {preview ? (
            <p className="text-xs text-fg-subtle mt-2 leading-relaxed">{preview}</p>
          ) : null}
          {rule.tags.length > 0 ? (
            <div className="mt-2 flex items-center gap-1.5 flex-wrap">
              {rule.tags.map((t) => (
                <span
                  key={t}
                  className="orb-badge border bg-bg-inset text-fg-muted border-border"
                >
                  {t}
                </span>
              ))}
            </div>
          ) : null}
        </div>

        {/* Install button area */}
        <div className="shrink-0 self-center flex flex-col items-end gap-1">
          {installed ? (
            <span className="inline-flex items-center gap-1 text-xs text-success font-medium">
              <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
              Installed
            </span>
          ) : (
            <button
              type="button"
              onClick={onInstall}
              disabled={installing}
              className="
                inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium
                bg-accent/10 text-accent border border-accent/30
                hover:bg-accent/20 disabled:opacity-50 disabled:cursor-not-allowed
                transition-colors
              "
            >
              {installing ? (
                <>
                  <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
                  Installing…
                </>
              ) : (
                'Install'
              )}
            </button>
          )}
          {installError ? (
            <p className="text-xs text-danger max-w-[8rem] text-right leading-tight">
              {installError}
            </p>
          ) : null}
        </div>
      </div>
    </li>
  );
}

function MarketplaceEmpty({ hasQuery }: { readonly hasQuery: boolean }) {
  if (hasQuery) {
    return (
      <div className="orb-card p-8 text-center">
        <div className="text-fg-subtle inline-flex items-center justify-center mb-2">
          <Search className="h-5 w-5" aria-hidden="true" />
        </div>
        <h2 className="font-display text-lg font-semibold text-fg mb-2">
          No marketplace rules match.
        </h2>
        <p className="text-fg-muted text-sm max-w-md mx-auto">
          Try a broader search term or clear the query to see all available rules.
        </p>
      </div>
    );
  }
  return (
    <div className="orb-card p-8 text-center">
      <div className="text-fg-subtle inline-flex items-center justify-center mb-2">
        <Store className="h-5 w-5" aria-hidden="true" />
      </div>
      <h2 className="font-display text-lg font-semibold text-fg mb-2">
        Marketplace catalog is empty.
      </h2>
      <p className="text-fg-muted text-sm max-w-md mx-auto">
        No rules have been synced yet. Run the{' '}
        <span className="orb-code">marketplace-sync</span> job or wait for the
        daily CronJob to populate the catalog.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Grouped rule list — T154 main addition
// ---------------------------------------------------------------------------

function GroupedRuleList({ rules }: { readonly rules: RuleSummary[] }) {
  const groups = useMemo(() => computeFamilyGroups(rules), [rules]);
  const [collapsed, setCollapsed] = useState<Set<FamilyKey>>(new Set());

  const toggle = (key: FamilyKey) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const allCollapsed = groups.length > 0 && groups.every((g) => collapsed.has(g.family.key));

  const toggleAll = () => {
    if (allCollapsed) {
      setCollapsed(new Set());
    } else {
      setCollapsed(new Set(groups.map((g) => g.family.key)));
    }
  };

  return (
    <div>
      {/* Icon legend + collapse-all control */}
      <div className="flex items-center justify-between gap-4 mb-3 flex-wrap">
        <IconLegend />
        <button
          type="button"
          onClick={toggleAll}
          className="text-xs text-fg-muted hover:text-fg transition-colors shrink-0"
        >
          {allCollapsed ? 'Expand all' : 'Collapse all'}
        </button>
      </div>

      <div className="space-y-2">
        {groups.map((group) => (
          <FamilySection
            key={group.family.key}
            group={group}
            collapsed={collapsed.has(group.family.key)}
            onToggle={() => toggle(group.family.key)}
          />
        ))}
      </div>
    </div>
  );
}

// Icon legend — compact row mapping each family icon to its name. Shown once
// above the family sections so users can parse the icons at a glance.
function IconLegend() {
  return (
    <div
      className="flex items-center gap-x-4 gap-y-1 flex-wrap"
      aria-label="Language family legend"
    >
      {FAMILY_META.map((f) => (
        <span
          key={f.key}
          title={f.description}
          className="inline-flex items-center gap-1 text-xs text-fg-muted"
        >
          <span className="text-fg-subtle" aria-hidden="true">
            {f.icon}
          </span>
          {f.label}
        </span>
      ))}
    </div>
  );
}

// Collapsible section for one language family.
function FamilySection({
  group,
  collapsed,
  onToggle,
}: {
  readonly group: FamilyGroup;
  readonly collapsed: boolean;
  readonly onToggle: () => void;
}) {
  return (
    <div className="orb-card overflow-hidden">
      {/* Section header — always visible, toggle expands/collapses the list */}
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={!collapsed}
        className="w-full px-5 py-3 flex items-center gap-3 text-left hover:bg-bg-inset transition-colors"
      >
        {/* Family icon */}
        <span className="text-fg-muted shrink-0" aria-hidden="true">
          {group.family.icon}
        </span>

        {/* Family name + total + per-language breakdown */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm text-fg">{group.family.label}</span>
            <span className="orb-badge border bg-bg-inset text-fg-muted border-border">
              {group.rules.length}
            </span>
            {/* Per-language counts — the primary feature added by T154 */}
            <div className="flex items-center gap-2 flex-wrap">
              {group.langCounts.map(({ lang, count }) => (
                <span key={lang} className="text-xs text-fg-subtle">
                  <span className="text-fg-muted">{LANGUAGE_LABEL[lang] ?? lang}</span>
                  {' '}
                  <span>{count}</span>
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Expand / collapse chevron */}
        <span className="shrink-0 text-fg-subtle" aria-hidden="true">
          {collapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
        </span>
      </button>

      {/* Rule list — hidden when collapsed */}
      {collapsed ? null : (
        <div className="border-t border-border">
          <RuleList rules={group.rules} nested />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Filter bar
// ---------------------------------------------------------------------------

interface FilterBarProps {
  readonly q: string;
  readonly onQ: (value: string) => void;
  readonly language: string;
  readonly onLanguage: (value: string) => void;
  readonly facets: RuleFacets;
  readonly severity?: IssueSeverity;
  readonly onSeverity: (value: IssueSeverity | undefined) => void;
  readonly kind?: IssueKind;
  readonly onKind: (value: IssueKind | undefined) => void;
}

// facetCountFor looks up a bucket's count for one value, 0 if the current
// query has no rules with that value at all (still rendered so the chip
// stays clickable — a 0-count facet e.g. becomes non-zero once other
// filters are loosened).
function facetCountFor(counts: readonly { value: string; count: number }[], value: string): number {
  return counts.find((c) => c.value === value)?.count ?? 0;
}

function FilterBar({
  q,
  onQ,
  language,
  onLanguage,
  facets,
  severity,
  onSeverity,
  kind,
  onKind,
}: FilterBarProps) {
  return (
    <section
      aria-label="Rule filters"
      className="orb-card p-4 space-y-4"
    >
      {/* Row 1: free-text search */}
      <label className="block">
        <span className="orb-section-title mb-1.5 inline-flex items-center gap-1.5 text-fg-muted">
          <Search className="h-3.5 w-3.5" aria-hidden="true" />
          Search
        </span>
        <input
          type="search"
          value={q}
          onChange={(e) => onQ(e.target.value)}
          placeholder="rule id, keyword, tag…"
          className="
            w-full px-3 py-2 rounded-md text-sm
            bg-bg-inset border border-border text-fg
            placeholder:text-fg-subtle
            focus:border-accent/60 focus:bg-bg-elevated
            transition-colors
          "
          aria-label="Search rules"
        />
      </label>

      <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
        <div className="inline-flex items-center gap-1.5 text-fg-muted">
          <Filter className="h-3.5 w-3.5" aria-hidden="true" />
          <span className="orb-section-title">Filters</span>
        </div>

        {facets.language.length > 0 ? (
          <FilterGroup label="Language">
            {facets.language.map((s) => (
              <LanguageChip
                key={s.value}
                value={s.value}
                count={s.count}
                active={language === s.value}
                onClick={() => onLanguage(language === s.value ? '' : s.value)}
              />
            ))}
          </FilterGroup>
        ) : null}

        <FilterGroup label="Severity">
          {ISSUE_SEVERITIES.map((s) => (
            <SeverityChip
              key={s}
              value={s}
              count={facetCountFor(facets.severity, s)}
              active={severity === s}
              onClick={() => onSeverity(severity === s ? undefined : s)}
            />
          ))}
        </FilterGroup>

        <FilterGroup label="Kind">
          {ISSUE_KINDS.map((k) => (
            <KindChip
              key={k}
              value={k}
              count={facetCountFor(facets.kind, k)}
              active={kind === k}
              onClick={() => onKind(kind === k ? undefined : k)}
            />
          ))}
        </FilterGroup>
      </div>
    </section>
  );
}

function FilterGroup({
  label,
  children,
}: {
  readonly label: string;
  readonly children: ReactNode;
}) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="orb-section-title">{label}</span>
      <div className="flex items-center gap-1.5 flex-wrap">{children}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Chips
// ---------------------------------------------------------------------------

interface ChipBtnProps {
  readonly active: boolean;
  readonly label: string;
  readonly toneActive: string;
  readonly toneIdle?: string;
  readonly onClick: () => void;
  readonly title?: string;
  readonly icon?: ReactNode;
}

function ChipBtn({
  active,
  label,
  toneActive,
  toneIdle,
  onClick,
  title,
  icon,
}: ChipBtnProps) {
  const idle =
    toneIdle ??
    'bg-bg-inset text-fg-muted border-border hover:text-fg hover:bg-bg-hover';
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      title={title}
      className={[
        'orb-badge border cursor-pointer transition-colors',
        active ? toneActive : idle,
      ].join(' ')}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}

function LanguageChip({
  value,
  count,
  active,
  onClick,
}: {
  readonly value: string;
  readonly count: number;
  readonly active: boolean;
  readonly onClick: () => void;
}) {
  const label = LANGUAGE_LABEL[value] ?? value;
  return (
    <ChipBtn
      active={active}
      label={`${label} · ${count}`}
      toneActive="bg-nebula/15 text-nebula border-nebula/40"
      onClick={onClick}
      title={
        value === '*'
          ? 'Rules that run against every file'
          : `Filter by ${label} rules`
      }
    />
  );
}

function SeverityChip({
  value,
  count,
  active,
  onClick,
}: {
  readonly value: IssueSeverity;
  readonly count?: number;
  readonly active: boolean;
  readonly onClick: () => void;
}) {
  return (
    <ChipBtn
      active={active}
      label={value}
      // Facet count shown as a title suffix rather than in the visible
      // label — keeps the accessible name stable ("BLOCKER", not
      // "BLOCKER · 21") for Playwright's exact-name button matching, while
      // still surfacing the count from GET /api/v1/rules's facets (T838)
      // on hover.
      title={
        count
          ? `Filter by ${value.toLowerCase()} severity (${count})`
          : `Filter by ${value.toLowerCase()} severity`
      }
      toneActive={SEVERITY_TONE[value]}
      onClick={onClick}
    />
  );
}

function KindChip({
  value,
  count,
  active,
  onClick,
}: {
  readonly value: IssueKind;
  readonly count?: number;
  readonly active: boolean;
  readonly onClick: () => void;
}) {
  return (
    <ChipBtn
      active={active}
      label={KIND_LABEL[value]}
      toneActive="bg-nebula/15 text-nebula border-nebula/40"
      onClick={onClick}
      icon={KIND_ICON[value]}
      title={
        count
          ? `Filter by ${value.replace('_', ' ').toLowerCase()} (${count})`
          : `Filter by ${value.replace('_', ' ').toLowerCase()}`
      }
    />
  );
}

// ---------------------------------------------------------------------------
// List
// ---------------------------------------------------------------------------

function RuleList({ rules, nested = false }: { readonly rules: RuleSummary[]; readonly nested?: boolean }) {
  return (
    <ul
      className={nested ? 'divide-y divide-border' : 'orb-card divide-y divide-border'}
      aria-label="Rules"
    >
      {rules.map((r) => (
        <RuleRow key={r.id} rule={r} />
      ))}
    </ul>
  );
}

function RuleRow({ rule }: { readonly rule: RuleSummary }) {
  const [, setSearch] = useSearchParams();
  const severity = asSeverity(rule.severity);
  const kind = asKind(rule.kind);
  const tone = severity ? SEVERITY_TONE[severity] : SEVERITY_TONE.INFO;
  const langLabel = LANGUAGE_LABEL[rule.language] ?? rule.language;

  const openDetail = () => {
    setSearch(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('rule', rule.id);
        return next;
      },
      { replace: true },
    );
  };

  return (
    <li>
      <button
        type="button"
        onClick={openDetail}
        className="w-full px-5 py-4 text-sm text-left hover:bg-bg-inset transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50 focus-visible:ring-inset"
        aria-label={`View details for rule ${rule.id}`}
      >
        <div className="flex items-start gap-3 flex-wrap">
          <span className={`orb-badge border shrink-0 ${tone}`}>
            {severity ?? rule.severity ?? 'INFO'}
          </span>
          {kind ? (
            <span
              className="orb-badge border bg-nebula/10 text-nebula border-nebula/30 shrink-0"
              title={kind.replace('_', ' ').toLowerCase()}
            >
              {KIND_ICON[kind]}
              <span>{KIND_LABEL[kind]}</span>
            </span>
          ) : null}
          <span
            className="orb-badge border bg-bg-inset text-fg-muted border-border shrink-0"
            title={
              rule.language === '*'
                ? 'Runs against every file regardless of language'
                : `Language: ${langLabel}`
            }
          >
            {langLabel}
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-fg font-medium leading-snug">
              {rule.name || <span className="text-fg-subtle">(unnamed rule)</span>}
            </p>
            <p className="orb-code text-xs text-fg-muted mt-0.5">{rule.id}</p>
            <TagsRow rule={rule} />
          </div>
          {rule.effort_min ? (
            <span
              className="text-xs text-fg-subtle shrink-0 self-center"
              title="Estimated remediation effort"
            >
              {rule.effort_min} min
            </span>
          ) : null}
        </div>
      </button>
    </li>
  );
}

function TagsRow({ rule }: { readonly rule: RuleSummary }) {
  const tags = rule.tags ?? [];
  if (tags.length === 0) return null;
  return (
    <div className="mt-2 flex items-center gap-1.5 flex-wrap">
      {tags.map((t) => (
        <span
          key={`t-${t}`}
          className="orb-badge border bg-bg-inset text-fg-muted border-border"
        >
          {t}
        </span>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared bits (loading / error / empty)
// ---------------------------------------------------------------------------

function Loading({ label }: { readonly label: string }) {
  return (
    <div className="orb-card p-6 flex items-center justify-center gap-3 text-fg-muted">
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
      <span className="text-sm">{label}</span>
    </div>
  );
}

function RequestError({ error }: { readonly error: unknown }) {
  let msg: string;
  if (error instanceof ApiError) {
    msg = `${error.code}: ${error.message}`;
  } else if (error instanceof Error) {
    msg = error.message;
  } else {
    msg = 'Unknown error';
  }
  const hint =
    error instanceof ApiError && error.code === 'missing_org'
      ? 'Set VITE_DEV_ORG_ID for local dev, or sign in — redirecting to /login…'
      : null;
  return (
    <div role="alert" className="orb-card p-5 border-danger/40 bg-danger/5">
      <p className="orb-eyebrow text-danger mb-1">request failed</p>
      <p className="text-sm text-fg">{msg}</p>
      {hint ? <p className="text-xs text-fg-muted mt-2">{hint}</p> : null}
    </div>
  );
}

function EmptyState({
  hasFilters,
  totalRules,
}: {
  readonly hasFilters: boolean;
  readonly totalRules: number;
}) {
  if (hasFilters) {
    return (
      <div className="orb-card p-8 text-center">
        <div className="text-fg-subtle inline-flex items-center justify-center mb-2">
          <Search className="h-5 w-5" aria-hidden="true" />
        </div>
        <h2 className="font-display text-lg font-semibold text-fg mb-2">
          No rules match these filters.
        </h2>
        <p className="text-fg-muted text-sm max-w-md mx-auto">
          {totalRules > 0
            ? 'Loosen a chip or clear the search box to see more results.'
            : 'The catalog is empty in this build — no analyzers registered any rules.'}
        </p>
      </div>
    );
  }
  return (
    <div className="orb-card p-8 text-center">
      <div className="text-fg-subtle inline-flex items-center justify-center mb-2">
        <Book className="h-5 w-5" aria-hidden="true" />
      </div>
      <h2 className="font-display text-lg font-semibold text-fg mb-2">
        No rules in the catalog.
      </h2>
      <p className="text-fg-muted text-sm max-w-md mx-auto">
        The active server build doesn&rsquo;t register any analyzer rules.
        This is expected on a stripped-down test binary; on a normal build
        every analyzer in <span className="orb-code">registry.Default()</span>{' '}
        contributes rules at startup.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Rule detail panel (T284) — right-side drawer showing full rule documentation
// ---------------------------------------------------------------------------

/** Inline markdown renderer: paragraphs + inline `code` spans, no library needed. */
function RuleMarkdown({ text, muted = false }: { readonly text: string; readonly muted?: boolean }) {
  const paragraphs = useMemo(
    () =>
      text
        .split(/\n{2,}/)
        .map((p) => p.trim())
        .filter((p) => p.length > 0),
    [text],
  );
  if (paragraphs.length === 0) return null;
  return (
    <div className="space-y-2">
      {paragraphs.map((p) => (
        <p key={p} className={`text-sm leading-relaxed ${muted ? 'text-fg-muted' : 'text-fg'}`}>
          {renderRuleInline(p)}
        </p>
      ))}
    </div>
  );
}

function renderRuleInline(text: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  let last = 0;
  let key = 0;
  for (const m of text.matchAll(/`([^`]+)`/g)) {
    const idx = m.index ?? 0;
    if (idx > last) parts.push(text.slice(last, idx));
    parts.push(
      <code
        key={`c-${key++}`}
        className="orb-code text-xs px-1 py-0.5 rounded bg-bg-inset border border-border"
      >
        {m[1]}
      </code>,
    );
    last = idx + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

/** Code block — renders noncompliant / compliant examples as a <pre> block. */
function CodeBlock({ code, label, variant }: { readonly code: string; readonly label: string; readonly variant: 'danger' | 'success' }) {
  const borderColor = variant === 'danger' ? 'border-danger/40' : 'border-success/40';
  const labelColor = variant === 'danger' ? 'text-danger' : 'text-success';
  return (
    <div>
      <p className={`orb-section-title mb-1.5 ${labelColor}`}>{label}</p>
      <pre
        className={`text-xs text-fg font-mono leading-relaxed bg-bg-inset border ${borderColor} rounded-md p-3 overflow-x-auto whitespace-pre-wrap break-words`}
      >
        <code>{code.trim()}</code>
      </pre>
    </div>
  );
}

function RuleDetailPanel({
  rule,
  isLoading,
  ruleID,
  onClose,
}: {
  readonly rule: Rule | undefined;
  readonly isLoading: boolean;
  readonly ruleID: string;
  readonly onClose: () => void;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const d = dialogRef.current;
    if (!d) return;
    if (!d.open) d.showModal();
    return () => {
      if (d.open) d.close();
    };
  }, []);

  return (
    <dialog
      ref={dialogRef}
      onClose={onClose}
      aria-label="Rule detail"
      className="
        bg-transparent p-0 m-0
        max-w-none max-h-none w-full h-full
        backdrop:bg-bg-base/60 backdrop:backdrop-blur-sm
      "
    >
      <div className="h-full w-full flex">
        <button
          type="button"
          onClick={onClose}
          aria-hidden="true"
          tabIndex={-1}
          className="flex-1 h-full cursor-default bg-transparent border-none"
        />
        <div className="ml-auto h-full w-full max-w-2xl bg-bg-elevated border-l border-border shadow-2xl flex flex-col overflow-hidden">
          {(() => {
            if (rule) return <RuleDetailBody rule={rule} onClose={onClose} />;
            if (isLoading) return <RuleDetailLoading onClose={onClose} />;
            return <RuleDetailMissing ruleID={ruleID} onClose={onClose} />;
          })()}
        </div>
      </div>
    </dialog>
  );
}

/** Renders a rule's remediation cost, honoring T950's size-sensitive
 *  remediation functions (mirrors api/internal/rulesdoc's effortLabel).
 *  Returns null when there's nothing to show (no effort_min, and no
 *  remediation function that would imply a per-gap-unit cost instead). */
function effortLabel(rule: Rule): string | null {
  const unit = rule.gap_unit_label || 'unit';
  switch (rule.remediation_function) {
    case 'LINEAR':
      return `${rule.gap_multiplier_min ?? 0} min/${unit}`;
    case 'LINEAR_OFFSET':
      return `${rule.effort_min ?? 0} min + ${rule.gap_multiplier_min ?? 0} min/${unit}`;
    default:
      return rule.effort_min ? `${rule.effort_min} min` : null;
  }
}

function RuleDetailHeader({
  rule,
  onClose,
}: {
  readonly rule: Rule;
  readonly onClose: () => void;
}) {
  const severity = asSeverity(rule.severity);
  const kind = asKind(rule.kind);
  const tone = severity ? SEVERITY_TONE[severity] : SEVERITY_TONE.INFO;
  const langLabel = LANGUAGE_LABEL[rule.language] ?? rule.language;

  return (
    <div className="sticky top-0 z-10 bg-bg-elevated border-b border-border px-6 pt-5 pb-4 shrink-0">
      <div className="flex items-start gap-2 mb-3">
        <div className="flex items-center gap-1.5 flex-wrap flex-1 min-w-0">
          <span className={`orb-badge border shrink-0 ${tone}`}>
            {severity ?? rule.severity ?? 'INFO'}
          </span>
          {kind ? (
            <span className="orb-badge border bg-nebula/10 text-nebula border-nebula/30 shrink-0">
              {KIND_ICON[kind]}
              <span>{KIND_LABEL[kind]}</span>
            </span>
          ) : null}
          <span className="orb-badge border bg-bg-inset text-fg-muted border-border shrink-0">
            {langLabel}
          </span>
          {rule.software_quality ? (
            <span className="orb-badge border bg-bg-inset text-fg-muted border-border shrink-0">
              {rule.software_quality}
            </span>
          ) : null}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="shrink-0 p-1.5 rounded-md text-fg-muted hover:text-fg hover:bg-bg-inset transition-colors"
          aria-label="Close rule detail"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
      <h2 className="font-display text-xl font-semibold text-fg leading-snug">
        {rule.name || <span className="text-fg-muted">(unnamed rule)</span>}
      </h2>
      <p className="orb-code text-xs text-fg-muted mt-1">{rule.id}</p>
      {effortLabel(rule) ? (
        <p className="text-xs text-fg-muted mt-1">
          Estimated effort: <span className="text-fg">{effortLabel(rule)}</span>
        </p>
      ) : null}
    </div>
  );
}

function RuleDetailBody({
  rule,
  onClose,
}: {
  readonly rule: Rule;
  readonly onClose: () => void;
}) {
  return (
    <>
      <RuleDetailHeader rule={rule} onClose={onClose} />
      <div className="flex-1 overflow-y-auto p-6 space-y-6">

        {/* Description */}
        {rule.description ? (
          <section>
            <div className="flex items-center gap-2 mb-2">
              <BookOpen className="h-3.5 w-3.5 text-fg-muted" aria-hidden="true" />
              <span className="orb-section-title">Description</span>
            </div>
            <RuleMarkdown text={rule.description} />
          </section>
        ) : null}

        {/* Why is this an issue */}
        {rule.why_it_matters ? (
          <section>
            <p className="orb-section-title mb-2">Why is this an issue?</p>
            <RuleMarkdown text={rule.why_it_matters} muted />
          </section>
        ) : null}

        {/* Code examples */}
        {rule.noncompliant_example || rule.compliant_example ? (
          <section className="space-y-4">
            <p className="orb-section-title">Code examples</p>
            {rule.noncompliant_example ? (
              <CodeBlock
                code={rule.noncompliant_example}
                label="Noncompliant"
                variant="danger"
              />
            ) : null}
            {rule.compliant_example ? (
              <CodeBlock
                code={rule.compliant_example}
                label="Compliant"
                variant="success"
              />
            ) : null}
          </section>
        ) : null}

        {/* How to fix */}
        {rule.how_to_fix ? (
          <section>
            <p className="orb-section-title mb-2">How to fix it</p>
            <RuleMarkdown text={rule.how_to_fix} muted />
          </section>
        ) : null}

        {/* Severity rationale */}
        {rule.severity_rationale ? (
          <section>
            <p className="orb-section-title mb-2">Severity rationale</p>
            <p className="text-sm text-fg-muted leading-relaxed">{rule.severity_rationale}</p>
          </section>
        ) : null}

        {/* Tags, CWE, OWASP */}
        {((rule.tags?.length ?? 0) > 0 ||
          (rule.cwe?.length ?? 0) > 0 ||
          (rule.owasp?.length ?? 0) > 0) ? (
          <section>
            <p className="orb-section-title mb-2">Classifications</p>
            <div className="flex items-center gap-1.5 flex-wrap">
              {rule.tags?.map((t) => (
                <span key={`t-${t}`} className="orb-badge border bg-bg-inset text-fg-muted border-border">
                  {t}
                </span>
              ))}
              {rule.cwe?.map((c) => (
                <span key={`cwe-${c}`} className="orb-badge border bg-bg-inset text-fg-muted border-border">
                  CWE-{c}
                </span>
              ))}
              {rule.owasp?.map((o) => (
                <span key={`owasp-${o}`} className="orb-badge border bg-bg-inset text-fg-muted border-border">
                  {o}
                </span>
              ))}
              {rule.clean_code_attribute ? (
                <span className="orb-badge border bg-bg-inset text-fg-muted border-border">
                  {rule.clean_code_attribute}
                </span>
              ) : null}
            </div>
          </section>
        ) : null}

        {/* References */}
        {rule.references && rule.references.length > 0 ? (
          <section>
            <p className="orb-section-title mb-2">References</p>
            <ul className="space-y-1">
              {rule.references.map((ref) => {
                const urlMatch = ref.match(/https?:\/\/\S+/);
                return (
                  <li key={ref} className="text-sm text-fg-muted flex items-start gap-1.5">
                    <ExternalLink className="h-3.5 w-3.5 shrink-0 mt-0.5 text-fg-subtle" aria-hidden="true" />
                    {urlMatch ? (
                      <span>
                        {ref.slice(0, urlMatch.index).trim()}{' '}
                        <a
                          href={safeHref(urlMatch[0])}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-accent hover:underline"
                        >
                          {urlMatch[0]}
                        </a>
                      </span>
                    ) : (
                      <span>{ref}</span>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        ) : null}

      </div>
    </>
  );
}

// Shown while GET /api/v1/rules/{ruleID} is in flight (T838 — the detail
// panel no longer reads from the already-loaded list, since the lean list
// DTO has no documentation prose to show).
function RuleDetailLoading({ onClose }: { readonly onClose: () => void }) {
  return (
    <>
      <div className="sticky top-0 z-10 bg-bg-elevated border-b border-border px-6 py-4 flex items-center justify-between shrink-0">
        <span className="orb-section-title">Rule detail</span>
        <button
          type="button"
          onClick={onClose}
          className="p-1.5 rounded-md text-fg-muted hover:text-fg hover:bg-bg-inset transition-colors"
          aria-label="Close"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
      <div className="flex-1 flex items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin text-fg-muted" aria-hidden="true" />
      </div>
    </>
  );
}

function RuleDetailMissing({
  ruleID,
  onClose,
}: {
  readonly ruleID: string;
  readonly onClose: () => void;
}) {
  return (
    <>
      <div className="sticky top-0 z-10 bg-bg-elevated border-b border-border px-6 py-4 flex items-center justify-between shrink-0">
        <span className="orb-section-title">Rule detail</span>
        <button
          type="button"
          onClick={onClose}
          className="p-1.5 rounded-md text-fg-muted hover:text-fg hover:bg-bg-inset transition-colors"
          aria-label="Close"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
      <div className="flex-1 flex items-center justify-center p-8 text-center">
        <div>
          <Book className="h-8 w-8 text-fg-subtle mx-auto mb-3" aria-hidden="true" />
          <p className="text-fg font-medium mb-1">Rule not found in catalog.</p>
          <p className="orb-code text-xs text-fg-muted">{ruleID}</p>
          <p className="text-xs text-fg-muted mt-3">
            This rule may not exist in the active server build.
          </p>
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const SEVERITY_SET = new Set<IssueSeverity>([
  'INFO',
  'MINOR',
  'MAJOR',
  'CRITICAL',
  'BLOCKER',
]);
const KIND_SET = new Set<IssueKind>([
  'BUG',
  'CODE_SMELL',
  'VULNERABILITY',
  'SECURITY_HOTSPOT',
  'SECRET',
]);

function parseSeverity(raw: string | null): IssueSeverity | undefined {
  if (!raw) return undefined;
  const up = raw.toUpperCase() as IssueSeverity;
  return SEVERITY_SET.has(up) ? up : undefined;
}

function parseKind(raw: string | null): IssueKind | undefined {
  if (!raw) return undefined;
  const up = raw.toUpperCase() as IssueKind;
  return KIND_SET.has(up) ? up : undefined;
}

function asSeverity(raw: string): IssueSeverity | undefined {
  return parseSeverity(raw);
}

function asKind(raw: string): IssueKind | undefined {
  return parseKind(raw);
}

/** Pull out the first paragraph of a rule's description for a list preview.
 *  Matches the markdown shape the issue-detail drawer renders — paragraphs
 *  are split on blank lines — and strips backticks so the preview reads
 *  cleanly without monospace inline code styling on the row. */
function firstParagraph(text: string): string {
  if (!text) return '';
  const para = text.split(/\n{2,}/)[0] ?? '';
  return para.trim().replaceAll('`', '');
}
